import { Plugins } from '@leverege/plugin';
import { Toast } from '@leverege/ui-elements';
import { GlobalState } from '@leverege/ui-redux';
import { Relationship, GroupDelegate, DeviceUtil } from '@leverege/molten/imagine';
import I18N from '@leverege/i18n';
import { getAssetBlueprintName, getAssetIdFromTag, getAssetType, getTagBlueprintName, getTagType } from "./Relationship.js";
const {
  t
} = I18N.ns('pairing.pairing.util.Context');
export function getRelationship(context) {
  const {
    clientState,
    clientProps
  } = context;
  return clientProps?.relationship || clientState?.actionsContext?.clientProps?.relationship;
}
function getAssetDelegate(context, relationship) {
  const {
    clientState,
    clientProps
  } = context;
  const delegate = clientProps?.delegate || clientState?.actionsContext?.clientProps?.delegate;
  if (delegate instanceof GroupDelegate) {
    return delegate;
  }
  if (delegate) {
    return delegate?.parent();
  }
  return null;
}
function getTagDelegate(context, relationship) {
  const {
    clientState,
    clientProps
  } = context;
  const delegate = clientProps?.delegate || clientState?.actionsContext?.clientProps?.delegate;
  const {
    attribute,
    blueprint
  } = relationship;
  const parentDelegate = delegate instanceof GroupDelegate ? delegate?.parent() : delegate?.parent()?.parent();
  let defaultTagDelegate;
  if (!parentDelegate) {
    const rel = Relationship.getByPath(blueprint.type);
    const match = {
      ...delegate.match,
      path: rel.urlPath
    };
    defaultTagDelegate = new GroupDelegate({
      relationship: rel.createResolved(match),
      match
    });
  } else {
    defaultTagDelegate = parentDelegate.child(`${attribute.path}s`);
    defaultTagDelegate = defaultTagDelegate || delegate;
  }
  return defaultTagDelegate;
}
const formFactory = Plugins.getFactory('BlueprintForm');
export function getSharedProps(context, relationship) {
  return {
    ...getAddForms(context, relationship),
    onPair: getOnPair(context, relationship),
    onUnpair: getOnUnpair(context, relationship)
  };
}
function getOnPair(context, relationship) {
  const {
    attribute
  } = relationship;
  return ({
    data: {
      asset,
      tag
    }
  }) => {
    const assetsActions = getAssetDelegate(context);
    const assetActions = assetsActions.item(asset.id);
    return GlobalState.dispatch(assetActions.child(attribute.path).create({
      id: tag.id,
      force: true
    }));
  };
}
function getOnUnpair(context, relationship) {
  const {
    attribute
  } = relationship;
  return async ({
    data
  }) => {
    const {
      tag
    } = data;
    let {
      asset
    } = data;
    const assetsActions = getAssetDelegate(context);
    if (!asset?.id) {
      const assetId = getAssetIdFromTag(tag, relationship);
      asset = await assetsActions.item(assetId);
    }
    const assetActions = assetsActions.item(asset.id);
    return GlobalState.dispatch(assetActions.child(attribute.path).delete());
  };
}
export function getAddForms(context, relationship) {
  return {
    AddAssetForm: getAddAssetForm(context, relationship),
    AddTagForm: getAddTagForm(context, relationship)
  };
}
export function getAddAssetForm(context, relationship) {
  const actions = getAssetDelegate(context, relationship);
  const parentBpName = getAssetBlueprintName(relationship);
  async function onSubmit({
    data: props,
    value
  }) {
    const {
      onSubmitComplete
    } = props;
    try {
      const res = await GlobalState.dispatch(actions.create(value));
      Toast.success(t('toastSuccess', {
        bpType: parentBpName
      }));
      onSubmitComplete?.({
        value: res,
        data: props
      });
    } catch (err) {
      Toast.error([t('toastError', {
        bpType: parentBpName
      }), err.message]);
    }
  }
  return addProps => {
    const parentObjectType = getAssetType(relationship);
    return formFactory.create(`${parentObjectType}.create`, {
      target: {
        type: parentObjectType,
        data: {}
      },
      title: t('title', {
        bpType: parentBpName
      }),
      submit: t('submit', {
        bpType: parentBpName
      }),
      eventData: {
        ...context,
        ...addProps
      },
      onSubmit,
      ...addProps
    });
  };
}
export function getAddTagForm(context, relationship) {
  const actions = getTagDelegate(context, relationship);
  const childBpName = getTagBlueprintName(relationship);
  async function onSubmit({
    data: props,
    value
  }) {
    const {
      onSubmitComplete
    } = props;
    try {
      const res = await GlobalState.dispatch(actions.create(value));
      Toast.success(t('toastSuccess', {
        bpType: childBpName
      }));
      onSubmitComplete?.({
        value: res,
        data: props
      });
    } catch (err) {
      Toast.error([t('toastError', {
        bpType: childBpName
      }), err.message]);
    }
  }
  return addProps => {
    const childObjectType = getTagType(relationship);
    return formFactory.create(`${childObjectType}.create`, {
      target: {
        type: childObjectType,
        data: {}
      },
      title: t('title', {
        bpType: childBpName
      }),
      submit: t('title', {
        bpType: childBpName
      }),
      eventData: {
        ...context,
        ...addProps
      },
      onSubmit,
      ...addProps
    });
  };
}
export { getTagDelegate, getAssetDelegate, getTagDelegate as getTagActions, getAssetDelegate as getAssetActions };